import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  fetchDocuments,
  fetchDocumentFields,
  fetchVerificationStatus,
  verifyField,
  recordCorrection,
  verifyAllRemaining,
  fetchDeal,
  fetchRuleSuggestions,
  createRule,
  regenerateFieldSources,
} from "../api/client";
import type {
  DocumentMeta,
  VerifiableField,
  VerificationStatus,
  RuleSuggestion,
} from "../api/client";
import VerifyProgressBar from "../components/verify/VerifyProgressBar";
import SourceViewer from "../components/verify/SourceViewer";
import FieldCardGroup from "../components/verify/FieldCardGroup";
import KeyboardHints from "../components/verify/KeyboardHints";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { useToastContext } from "../contexts/ToastContext";
import { friendlyError } from "../utils/errors";

export default function VerifyPage() {
  const { dealId } = useParams<{ dealId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const deepLinkDoc = searchParams.get("doc");
  const deepLinkField = searchParams.get("field");
  const toast = useToastContext();

  const [dealName, setDealName] = useState("");
  const [docs, setDocs] = useState<DocumentMeta[]>([]);
  const [activeDocId, setActiveDocId] = useState<string | null>(deepLinkDoc);
  const [fields, setFields] = useState<VerifiableField[]>([]);
  const [status, setStatus] = useState<VerificationStatus | null>(null);
  const [focusedPath, setFocusedPath] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<RuleSuggestion[]>([]);
  const [rulesApplied, setRulesApplied] = useState<Record<string, string>>({});
  const [showTour, setShowTour] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const [regenerating, setRegenerating] = useState(false);
  // Per-doc flag set after a regeneration attempt that did not produce real
  // source coverage. Swaps the banner from "Regenerate" to a non-actionable
  // explanation so the user isn't stuck clicking forever.
  const [regenAttempted, setRegenAttempted] = useState<Record<string, boolean>>({});
  // Per-doc coverage stats reported by the regenerate endpoint, used to
  // dismiss the banner without waiting for parse_result to round-trip.
  const [regenCoverage, setRegenCoverage] = useState<Record<string, { ai_inferred_pct: number; total_fields: number }>>({});
  // Per-doc flag preventing auto-regen from firing more than once per doc
  // per page visit (we still allow a manual click as a fallback).
  const [autoRegenFired, setAutoRegenFired] = useState<Record<string, boolean>>({});

  const runRegeneration = useCallback(
    async (docId: string, opts: { quiet?: boolean } = {}) => {
      if (!dealId) return;
      setRegenerating(true);
      try {
        const result = await regenerateFieldSources(dealId, docId);
        const refreshed = await fetchDocuments(dealId);
        const parsed = refreshed.filter((d) => d.parse_status === "complete");
        setDocs(parsed);
        if (docId === activeDocId) {
          const fieldsRes = await fetchDocumentFields(dealId, docId);
          setFields(fieldsRes.fields);
        }
        const cov = (result.coverage ?? {}) as { ai_inferred_pct?: number; total_fields?: number };
        const ai_inferred_pct = cov.ai_inferred_pct ?? 100;
        const total_fields = cov.total_fields ?? 0;
        setRegenCoverage((m) => ({ ...m, [docId]: { ai_inferred_pct, total_fields } }));
        const success = total_fields > 0 && ai_inferred_pct < 95;
        if (success) {
          if (!opts.quiet) {
            toast.info("Source highlights regenerated. Click any field to see its source.");
          }
          setRegenAttempted((m) => ({ ...m, [docId]: false }));
        } else {
          setRegenAttempted((m) => ({ ...m, [docId]: true }));
          if (!opts.quiet) {
            toast.info("Source locations could not be determined for this document.");
          }
        }
      } catch {
        setRegenAttempted((m) => ({ ...m, [docId]: true }));
        if (!opts.quiet) {
          toast.info("Could not regenerate source highlights for this document.");
        }
      } finally {
        setRegenerating(false);
      }
    },
    [dealId, activeDocId, toast],
  );

  const handleRegenerateSources = useCallback(async () => {
    if (!activeDocId) return;
    await runRegeneration(activeDocId);
  }, [activeDocId, runRegeneration]);

  // Onboarding tour (Fix 21) - show on first visit
  useEffect(() => {
    if (!localStorage.getItem("verify_tour_completed")) {
      setShowTour(true);
    }
  }, []);

  const dismissTour = () => {
    setShowTour(false);
    localStorage.setItem("verify_tour_completed", "true");
  };

  // Memoization toast (Fix 20)
  useEffect(() => {
    if (docs.length === 0) return;
    const memoizedDoc = docs.find((d) => {
      const pr = d.parse_result as Record<string, unknown> | null;
      const ep = pr?.extraction_path;
      return ep === "memoized_exact" || ep === "memoized_structural";
    });
    if (memoizedDoc) {
      toast.info("This layout looks familiar \u2014 we pre-filled the verified fields.");
    }
  }, [docs]);

  // Load deal info, documents, and verification status
  useEffect(() => {
    if (!dealId) return;
    let cancelled = false;

    async function load() {
      setLoading(true);
      setLoadError(null);
      try {
        const [deal, allDocs, vStatus] = await Promise.all([
          fetchDeal(dealId!),
          fetchDocuments(dealId!),
          fetchVerificationStatus(dealId!),
        ]);
        if (cancelled) return;
        setDealName(deal.name);
        const parsed = allDocs.filter((d) => d.parse_status === "complete");
        setDocs(parsed);
        setStatus(vStatus);
        if (parsed.length > 0 && !activeDocId) {
          setActiveDocId(parsed[0].id);
        }
      } catch (e) {
        if (!cancelled) {
          const msg = e instanceof Error ? e.message : String(e);
          setLoadError(friendlyError(msg));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [dealId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-regenerate source highlights once per visit for legacy docs that
  // were parsed before source-capture shipped (or whose locator results
  // were lost). Fires silently in the background — no banner, no toast,
  // no user action required. The explicit Regenerate button stays as a
  // fallback for documents this pass can't help with.
  //
  // Also fires when the stored locator_version is older than the current
  // one, so documents upgrade to locator improvements (added column
  // coverage, better value matching) on the next Verify visit instead of
  // requiring a manual Regenerate click. Kept in sync with
  // parser.field_sources.FIELD_SOURCES_VERSION.
  const CURRENT_LOCATOR_VERSION = 2;
  useEffect(() => {
    if (!activeDocId) return;
    const doc = docs.find((d) => d.id === activeDocId);
    if (!doc) return;
    if (autoRegenFired[activeDocId]) return;
    const pr = doc.parse_result as Record<string, unknown> | null;
    const cov = pr?._source_coverage as
      | {
          ai_inferred_pct?: number;
          total_fields?: number;
          is_scanned?: boolean;
          locator_version?: number;
        }
      | undefined;
    const needsRegen =
      !cov
      || (cov.total_fields ?? 0) === 0
      || (cov.ai_inferred_pct ?? 100) >= 95
      || (cov.locator_version ?? 0) < CURRENT_LOCATOR_VERSION;
    if (!needsRegen || cov?.is_scanned) return;
    setAutoRegenFired((m) => ({ ...m, [activeDocId]: true }));
    runRegeneration(activeDocId, { quiet: true });
  }, [activeDocId, docs, autoRegenFired, runRegeneration]);

  // Load fields and suggestions when active doc changes
  useEffect(() => {
    if (!dealId || !activeDocId) return;
    let cancelled = false;

    fetchDocumentFields(dealId, activeDocId)
      .then((res) => {
        if (!cancelled) {
          setFields(res.fields);
          // Deep-link: focus the requested field, otherwise clear
          setFocusedPath(deepLinkField || null);
        }
      })
      .catch(() => {});

    // Fetch rule suggestions
    fetchRuleSuggestions(dealId, activeDocId)
      .then((res) => {
        if (!cancelled) setSuggestions(res);
      })
      .catch(() => {});

    // Extract rules_applied from parse result
    const activeDoc = docs.find((d) => d.id === activeDocId);
    const applied = (activeDoc?.parse_result as Record<string, unknown>)?.rules_applied;
    if (Array.isArray(applied)) {
      const map: Record<string, string> = {};
      for (const name of applied) {
        if (typeof name === "string") map[name] = name;
      }
      setRulesApplied(map);
    } else {
      setRulesApplied({});
    }

    return () => { cancelled = true; };
  }, [dealId, activeDocId, docs]);

  // Refresh verification status
  const refreshStatus = useCallback(async () => {
    if (!dealId) return;
    try {
      const s = await fetchVerificationStatus(dealId);
      setStatus(s);
    } catch {}
  }, [dealId]);

  // Handlers
  const handleVerify = useCallback(
    async (fieldPath: string) => {
      if (!dealId || !activeDocId) return;
      try {
        await verifyField(dealId, activeDocId, fieldPath);
        setFields((prev) =>
          prev.map((f) =>
            f.field_path === fieldPath ? { ...f, state: "verified" } : f,
          ),
        );
        await refreshStatus();
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        toast.error(`Could not verify field: ${friendlyError(msg)}`);
      }
    },
    [dealId, activeDocId, refreshStatus, toast],
  );

  const handleCorrect = useCallback(
    async (fieldPath: string, newValue: unknown) => {
      if (!dealId || !activeDocId) return;
      try {
        await recordCorrection(dealId, activeDocId, fieldPath, newValue);
        setFields((prev) =>
          prev.map((f) =>
            f.field_path === fieldPath
              ? {
                  ...f,
                  state: "corrected",
                  original_value: f.value,
                  value: newValue,
                }
              : f,
          ),
        );
        await refreshStatus();
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        toast.error(`Could not save correction: ${friendlyError(msg)}`);
      }
    },
    [dealId, activeDocId, refreshStatus, toast],
  );

  const handleVerifyAll = useCallback(async () => {
    if (!dealId || !activeDocId) return;
    try {
      await verifyAllRemaining(dealId, activeDocId);
      setFields((prev) =>
        prev.map((f) =>
          f.state === "unverified" && !f.is_critical
            ? { ...f, state: "verified" }
            : f,
        ),
      );
      await refreshStatus();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(`Could not verify remaining fields: ${friendlyError(msg)}`);
    }
  }, [dealId, activeDocId, refreshStatus, toast]);

  const handleFlag = useCallback(
    async (fieldPath: string) => {
      if (!dealId || !activeDocId) return;
      try {
        await recordCorrection(dealId, activeDocId, fieldPath, null, "flag");
        setFields((prev) =>
          prev.map((f) =>
            f.field_path === fieldPath ? { ...f, state: "flag" } : f,
          ),
        );
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        toast.error(`Could not flag field: ${friendlyError(msg)}`);
      }
    },
    [dealId, activeDocId, toast],
  );

  const handleRevert = useCallback(
    async (fieldPath: string) => {
      if (!dealId || !activeDocId) return;
      const field = fields.find((f) => f.field_path === fieldPath);
      if (!field || field.original_value === null) return;
      try {
        await recordCorrection(dealId, activeDocId, fieldPath, field.original_value);
        setFields((prev) =>
          prev.map((f) =>
            f.field_path === fieldPath
              ? { ...f, state: "unverified", value: f.original_value, original_value: null }
              : f,
          ),
        );
        await refreshStatus();
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        toast.error(`Could not revert field: ${friendlyError(msg)}`);
      }
    },
    [dealId, activeDocId, fields, refreshStatus, toast],
  );

  const handleApproveSuggestion = useCallback(
    async (suggestion: RuleSuggestion) => {
      try {
        await createRule({
          ...suggestion.suggested_rule as {
            file_type: string;
            rule_type: string;
            match_criteria: Record<string, unknown>;
            action: Record<string, unknown>;
          },
          source_correction_ids: suggestion.source_correction_ids,
        });
        setSuggestions((prev) =>
          prev.filter((s) => s.field_path !== suggestion.field_path),
        );
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        toast.error(`Could not create rule: ${friendlyError(msg)}`);
      }
    },
    [toast],
  );

  const handleProceed = useCallback(() => {
    if (dealId) navigate(`/deals/${dealId}/cash-flow`);
  }, [dealId, navigate]);

  // Group fields by section
  const grouped = useMemo(() => {
    const map = new Map<string, VerifiableField[]>();
    for (const f of fields) {
      const section = f.section || "Other";
      const list = map.get(section) || [];
      list.push(f);
      map.set(section, list);
    }
    return map;
  }, [fields]);

  // Unverified field navigation
  const unverifiedPaths = useMemo(
    () => fields.filter((f) => f.state === "unverified").map((f) => f.field_path),
    [fields],
  );

  const unverifiedNonCritical = useMemo(
    () => fields.filter((f) => f.state === "unverified" && !f.is_critical).length,
    [fields],
  );

  // Keyboard nav: Tab moves to next unverified field
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Tab" && !e.ctrlKey && !e.metaKey) {
        if (unverifiedPaths.length === 0) return;
        e.preventDefault();
        const currentIdx = focusedPath
          ? unverifiedPaths.indexOf(focusedPath)
          : -1;
        const nextIdx = e.shiftKey
          ? (currentIdx - 1 + unverifiedPaths.length) % unverifiedPaths.length
          : (currentIdx + 1) % unverifiedPaths.length;
        setFocusedPath(unverifiedPaths[nextIdx]);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [focusedPath, unverifiedPaths]);

  const activeDoc = docs.find((d) => d.id === activeDocId);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-forest-600 border-t-transparent" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-bark-400">
        <AlertTriangle className="w-8 h-8 mb-3 text-red-500" />
        <p className="text-sm text-bark-600">Could not load verification data.</p>
        <p className="text-xs mt-1">{loadError}</p>
      </div>
    );
  }

  if (docs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-bark-400">
        <AlertTriangle className="w-8 h-8 mb-3" />
        <p className="text-sm">No parsed documents to verify.</p>
        <p className="text-xs mt-1">Upload and parse a document first.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Progress bar */}
      {status && (
        <VerifyProgressBar
          status={status}
          dealName={dealName}
          onProceed={handleProceed}
        />
      )}

      {/* Document tabs */}
      {docs.length > 1 && (
        <div className="flex items-center gap-1 px-4 sm:px-8 pt-3 pb-1 border-b border-grove-200 bg-white overflow-x-auto scrollbar-hide">
          {docs.map((doc) => (
            <button
              key={doc.id}
              onClick={() => setActiveDocId(doc.id)}
              className={`px-3 py-1.5 text-xs font-medium rounded-t-md transition-colors whitespace-nowrap ${
                doc.id === activeDocId
                  ? "bg-forest-50 text-forest-700 border border-grove-200 border-b-white -mb-px"
                  : "text-bark-400 hover:text-forest-600 hover:bg-grove-50"
              }`}
            >
              {doc.filename}
            </button>
          ))}
          {activeDoc && (
            <button
              onClick={handleRegenerateSources}
              disabled={regenerating}
              title="Force a fresh pass of the source locator for this document"
              className="ml-auto shrink-0 text-[11px] font-medium text-bark-400 hover:text-forest-700 transition-colors disabled:opacity-50"
            >
              {regenerating ? "Regenerating…" : "Regenerate"}
            </button>
          )}
        </div>
      )}

      {/* Source-coverage banner.
          - In-flight auto-regeneration → nothing (silent background pass).
          - Live regen response shows the doc now has real sources → nothing.
          - One regen attempt has finished and coverage is still missing
            (e.g. scanned PDF, unsupported workbook layout) → quiet note,
            no Regenerate button (one attempt is enough).
          - Otherwise (legacy doc with no auto-regen yet) → render nothing
            because auto-regen will fire silently; the manual button is
            only kept around for the explicit fallback case below. */}
      {activeDoc && (() => {
        // Live coverage from the latest regen response wins over the
        // (possibly stale) coverage embedded in parse_result.
        const live = regenCoverage[activeDoc.id];
        const pr = activeDoc.parse_result as Record<string, unknown> | null;
        const stored = pr?._source_coverage as
          | { ai_inferred_pct?: number; total_fields?: number; is_scanned?: boolean }
          | undefined;
        const isScanned = !!stored?.is_scanned;
        const liveOk = live && live.total_fields > 0 && live.ai_inferred_pct < 95;
        const storedOk =
          stored
          && (stored.total_fields ?? 0) > 0
          && (stored.ai_inferred_pct ?? 100) < 95;
        if (liveOk || storedOk) return null;
        const attempted = regenAttempted[activeDoc.id] || isScanned;
        if (attempted) {
          return (
            <div className="px-4 sm:px-8 py-2 bg-grove-50 border-b border-grove-200">
              <span className="text-xs text-bark-500">
                Source locations could not be determined for this document.
                {isScanned ? " The PDF appears to be scanned (no text layer)." : ""}
              </span>
            </div>
          );
        }
        // Auto-regen is firing or queued — show a small in-flight note so
        // the user understands the silence rather than confusion.
        if (autoRegenFired[activeDoc.id] && regenerating) {
          return (
            <div className="px-4 sm:px-8 py-2 bg-grove-50 border-b border-grove-200">
              <span className="text-xs text-bark-500">
                Loading source highlights…
              </span>
            </div>
          );
        }
        return null;
      })()}

      {/* Two-column layout */}
      <div className="flex-1 flex min-h-0">
        {/* Left: Source viewer (40%) */}
        <div className="w-[40%] min-w-[320px] p-4 border-r border-grove-200 overflow-hidden">
          {activeDoc && (
            <SourceViewer
              dealId={dealId!}
              docId={activeDoc.id}
              filename={activeDoc.filename}
              fileType={activeDoc.file_type}
              activeFieldPath={focusedPath}
            />
          )}
        </div>

        {/* Right: Field cards (60%) */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {Array.from(grouped.entries()).map(([section, sectionFields]) => (
            <FieldCardGroup
              key={section}
              section={section}
              fields={sectionFields}
              focusedPath={focusedPath}
              onVerify={handleVerify}
              onCorrect={handleCorrect}
              onFocus={setFocusedPath}
              onFlag={handleFlag}
              onRevert={handleRevert}
              rulesApplied={rulesApplied}
              suggestions={suggestions}
              onApproveSuggestion={handleApproveSuggestion}
            />
          ))}

          {/* Non-critical banner */}
          {unverifiedNonCritical > 0 && (
            <div className="sticky bottom-0 mt-4 px-4 py-3 bg-grove-100 border border-grove-300 rounded-lg flex items-center justify-between">
              <span className="text-sm text-bark-600">
                {unverifiedNonCritical} non-critical field
                {unverifiedNonCritical !== 1 ? "s" : ""} unverified
              </span>
              <button
                onClick={handleVerifyAll}
                className="px-3 py-1.5 text-xs font-medium rounded-md bg-forest-600 text-white hover:bg-forest-700 transition-colors"
              >
                Verify all remaining as-is
              </button>
            </div>
          )}

          {/* All done state */}
          {fields.length > 0 && unverifiedPaths.length === 0 && (
            <div className="flex items-center gap-2 mt-4 px-4 py-3 bg-sage-50 border border-sage-200 rounded-lg">
              <CheckCircle2 className="w-5 h-5 text-sage-600" />
              <span className="text-sm font-medium text-sage-700">
                All fields verified. You may proceed to cash flow.
              </span>
            </div>
          )}
        </div>
      </div>

      <KeyboardHints />

      {/* Onboarding tour (Fix 21) */}
      {showTour && (
        <div className="fixed inset-0 z-50 bg-bark-900/40 flex items-center justify-center" onClick={dismissTour}>
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm mx-4" onClick={(e) => e.stopPropagation()}>
            {tourStep === 0 && (
              <>
                <h3 className="font-display text-lg font-semibold text-bark-800 mb-2">Source Document</h3>
                <p className="text-sm text-bark-600 mb-4">The left pane shows your source document. Click any field on the right to see where the value came from.</p>
              </>
            )}
            {tourStep === 1 && (
              <>
                <h3 className="font-display text-lg font-semibold text-bark-800 mb-2">Verify Fields</h3>
                <p className="text-sm text-bark-600 mb-4">Each card represents a parsed value. Press Enter to verify, Shift+Enter to edit, or click the Flag button to mark for review.</p>
              </>
            )}
            {tourStep === 2 && (
              <>
                <h3 className="font-display text-lg font-semibold text-bark-800 mb-2">Keyboard Shortcuts</h3>
                <p className="text-sm text-bark-600 mb-4">Use Tab to jump between fields. The goal: verify 50 fields in under two minutes. Once all critical fields are verified, you can proceed to cash flow.</p>
              </>
            )}
            <div className="flex items-center justify-between">
              <span className="text-xs text-bark-400">{tourStep + 1} of 3</span>
              {tourStep < 2 ? (
                <button onClick={() => setTourStep(tourStep + 1)} className="px-4 py-2 text-sm font-medium rounded-lg bg-forest-600 text-white hover:bg-forest-700">
                  Next
                </button>
              ) : (
                <button onClick={dismissTour} className="px-4 py-2 text-sm font-medium rounded-lg bg-forest-600 text-white hover:bg-forest-700">
                  Got it
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
